import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, Plus, X } from '@phosphor-icons/react'
import { DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../../api/core'
import {
  lookupPackableBatch,
  useAllSkus,
  usePackingHalls,
  usePackRuns,
  type PackRunRow,
} from '../../api/packFull'
import { ActionButton } from '../../components/Can'
import { FilterBar, SearchFilter, SelectFilter } from '../../components/FilterBar'
import { ConsolePage } from '../../components/Layout'
import { Code, Kg, RefLink, Status, When } from '../../components/bits'
import { PlanRunDialog } from '../../components/pack/PlanRunDialog'
import { useUrlState } from '../../lib/useUrlState'
import { packLotStatusText, runState, RUN_STATUS_FILTER } from '../../model/pack/packWords'

const LIMIT = 50

/** Pack runs (F7.1, F7.2): planned, started and closed runs with their pack lots. */
export function PackRunsPage() {
  const navigate = useNavigate()
  const [f, setF, { reset, active }] = useUrlState({
    status: '',
    hall: '',
    batch: '',
    batchCode: '',
    page: 1,
  })
  const q = usePackRuns({
    status: f.status || undefined,
    locationId: f.hall || undefined,
    batchUnitId: f.batch || undefined,
    page: f.page,
    limit: LIMIT,
  })
  const skus = useAllSkus()
  const halls = usePackingHalls()
  const skuById = useMemo(() => new Map((skus.data ?? []).map((s) => [s.id, s])), [skus.data])
  const [planning, setPlanning] = useState(false)
  const [batchMiss, setBatchMiss] = useState<string | null>(null)
  const [batchText, setBatchText] = useState('')

  const findBatch = async (code: string) => {
    setBatchText(code)
    setBatchMiss(null)
    if (!code) {
      setF({ batch: '', batchCode: '' })
      return
    }
    try {
      const b = await lookupPackableBatch(code)
      if (b) setF({ batch: b.unitId, batchCode: b.code })
      else setBatchMiss(code)
    } catch {
      setBatchMiss(code)
    }
  }

  const columns: Column<PackRunRow>[] = [
    {
      id: 'code',
      header: 'Run',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <Code>{r.code}</Code>
          <span className="text-[12px] text-muted">
            {r.plannedDate ? <When at={r.plannedDate} dateOnly /> : '—'}
          </span>
        </span>
      ),
    },
    {
      id: 'sku',
      header: 'Product',
      cell: (r) => {
        const s = r.skuId ? skuById.get(r.skuId) : undefined
        return s ? (
          <span className="flex flex-col leading-tight">
            <span>{s.name}</span>
            <span className="font-mono text-[12px] text-muted">{s.code}</span>
          </span>
        ) : (
          <span className="text-muted">—</span>
        )
      },
    },
    {
      id: 'batch',
      header: 'Batch',
      cell: (r) =>
        r.batchUnitId ? (
          <RefLink to={`/c/batches/${r.batchUnitId}`}>{r.batchCode ?? 'Batch'}</RefLink>
        ) : (
          '—'
        ),
    },
    {
      id: 'planned',
      header: 'Planned',
      align: 'right',
      cell: (r) => (
        <span className="flex flex-col items-end whitespace-nowrap leading-tight">
          <Kg grams={r.plannedG} />
          <span className="font-mono text-[12px] text-muted">
            {r.plannedPouches !== null ? `${r.plannedPouches.toLocaleString('en-IN')} pouches` : ''}
          </span>
        </span>
      ),
    },
    {
      id: 'hall',
      header: 'Hall · line',
      hideBelow: 'lg',
      cell: (r) => (
        <span className="font-mono text-[12.5px]">
          {r.locationCode ?? '—'}
          {r.equipmentCode ? ` · ${r.equipmentCode}` : ''}
        </span>
      ),
    },
    {
      id: 'lot',
      header: 'Pack lot',
      cell: (r) =>
        r.packLotId ? (
          <span className="flex flex-col items-start gap-1 leading-tight">
            <RefLink to={`/c/pack-lots/${r.packLotId}`}>{r.packLotCode ?? 'Pack lot'}</RefLink>
            <span className="flex flex-wrap gap-1">
              {r.packLotStatus && (
                <Status status={r.packLotStatus} label={packLotStatusText(r.packLotStatus)} />
              )}
              {r.packLotHold && r.packLotHold !== 'NONE' && <Status status={r.packLotHold} />}
            </span>
          </span>
        ) : (
          <span className="text-[13px] text-muted">Made at start</span>
        ),
    },
    {
      id: 'status',
      header: 'Run',
      cell: (r) => {
        const s = runState(r.status, r.startedAt)
        return <Status status={s.status} label={s.label} />
      },
    },
  ]

  return (
    <ConsolePage
      eyebrow="Pack & lab"
      title="Pack runs"
      description="One batch, one product, one pack lot. Plan a run here; the line starts and closes it on the pack-hall phone, then the lot waits for release."
      actions={
        <ActionButton
          perm="pack-runs:create"
          leadingIcon={<Plus />}
          onClick={() => setPlanning(true)}
        >
          Plan a pack run
        </ActionButton>
      }
    >
      <DataTable<PackRunRow>
        caption="Pack runs"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/pack-runs/${r.id}`)}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        empty={
          <EmptyState
            icon={<Package />}
            title={active ? 'No pack runs match' : 'No pack runs yet'}
            description={
              active
                ? 'Try other filters.'
                : 'Plan the first run from a batch that is ready to pack.'
            }
            compact
          />
        }
        filters={
          <FilterBar
            onClear={() => {
              setBatchMiss(null)
              setBatchText('')
              reset()
            }}
            active={active}
          >
            <SelectFilter
              label="Status"
              value={f.status}
              onChange={(status) => setF({ status })}
              options={RUN_STATUS_FILTER}
            />
            <SelectFilter
              label="Packing hall"
              value={f.hall}
              onChange={(hall) => setF({ hall })}
              options={(halls.data ?? []).map((h) => ({ value: h.id, label: h.name }))}
            />
            {f.batch ? (
              <div className="flex flex-col gap-1">
                <span className="font-mono text-[10px] font-bold uppercase tracking-label text-muted">
                  Batch
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setBatchText('')
                    setF({ batch: '', batchCode: '' })
                  }}
                  className="inline-flex h-9 items-center gap-1.5 rounded-chip border border-rule bg-surface px-3 font-mono text-[13px] hover:border-ink/40"
                  aria-label={`Remove batch filter ${f.batchCode}`}
                >
                  {f.batchCode || 'Batch'} <X aria-hidden="true" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col">
                <SearchFilter
                  label="Batch code"
                  value={batchText}
                  onChange={(v) => void findBatch(v.toUpperCase())}
                  placeholder="e.g. B-2610-0004"
                  delay={600}
                />
                {batchMiss && (
                  <span className="mt-1 text-[12px] text-warn">No batch {batchMiss}</span>
                )}
              </div>
            )}
          </FilterBar>
        }
      />
      <PlanRunDialog open={planning} onClose={() => setPlanning(false)} />
    </ConsolePage>
  )
}
